import { GET_TRIAGE_TICKET_LIST } from '@nile/banyan/src/Graphql/Query/ai';
import {
  COMMON_CONSTANTS,
  useProduct,
  withErrorBoundary,
  UnauthorisedAccess,
  AccessControl,
  EventEmitter
} from '@nile/components';
import CardCarousel from '@nile/components/src/Components/CardCarousel';
import { TicketClient } from '@nile/graphql';
import TriageTicketCard from '@nile/mangifera/src/Containers/CardContainers/TriageTicketCard.tsx';
import React, { useState, useEffect } from 'react';
import {
  getAIPrompts,
  getTranslatedText,
  useIsAuthorisedHook
} from '@nile/components/src/Utils/helpers';
import envConfig from '@nile/home/src/Config/env';
import { SVGIcon } from '@superops/super-icons';
import { Heading, TextStyle } from '@superops/ui-design-system';
import AIFeaturesHook from '@nile/components/src/Utils/aiFeaturesHook';
import { FormattedMessage, useIntl } from 'react-intl';
import { TicketDisplayIdContainer } from '@nile/mangifera/src/Components/Ticket';
import PatchAgent from '@nile/mangifera/src/Containers/PageContainers/PatchAgent';
import { useLazyQuery } from '@apollo/client';
import { union } from 'lodash';

interface ActiveView {
  id: string;
  name: string;
}
interface MonicaAIReviewQueueProps {
  activeView: ActiveView;
}

const MonicaAIReviewQueue = (props: MonicaAIReviewQueueProps) => {
  const { activeView } = props;
  const [data, setData] = useState(null);
  const isMSPProduct = useProduct(COMMON_CONSTANTS.MSP);
  const aiFeatureHook = AIFeaturesHook();
  const intl = useIntl();

  const isTicketViewAuthorised = useIsAuthorisedHook('tickets.view');

  const isTriageAgentEnabled =
    aiFeatureHook?.getIsAIFeatureEnabled('TICKET_TRIAGE') || false;

  const [dislikedTickets, setDislikedTickets] = useState([]);

  const onFeedbackComplete = (newDislike) => {
    setDislikedTickets((prev) => {
      const index = prev.findIndex((t) => t.ticketId === newDislike.ticketId);

      // CASE 1: Ticket NOT found → add new
      if (index === -1) {
        return [...prev, newDislike];
      }

      // CASE 2: Ticket EXISTS → update
      const existing = prev[index];

      // Merge feedbackTypes uniquely using lodash
      const mergedFeedback = union(
        existing.feedbackType,
        newDislike.feedbackType
      );

      const updatedTicket = {
        ...existing,
        feedbackType: mergedFeedback,
        isDisliked: mergedFeedback.length > 1 ? true : existing.isDisliked
      };

      return prev.map((t, i) => (i === index ? updatedTicket : t));
    });
  };

  const AIPrompts = getAIPrompts();

  const CountRender = ({ count = 0, label, dotClassName, textVariation }) => {
    if (!Number.isFinite(count) || count <= 0) return null;

    const className = ['flex actr', dotClassName].filter(Boolean).join(' ');
    return (
      <div className="flex g5 actr">
        <div className={className}> </div>
        <div>
          <TextStyle variation={textVariation}>{count}</TextStyle>
        </div>
        <div>
          <TextStyle variation={textVariation}>
            <FormattedMessage id={label} />
          </TextStyle>
        </div>
      </div>
    );
  };

  const HeadingComponent = (props) => {
    const [getTriageCount, { data: triageTicketListData }] = useLazyQuery(
      GET_TRIAGE_TICKET_LIST,
      {
        variables: {
          listInfo: {
            sort: [
              {
                attribute: 'createdTime',
                order: 'DESC'
              }
            ],
            page: 1,
            pageSize: 3,
            condition: {
              joinOperator: 'AND',
              operands: [
                {
                  attribute: 'client.name',
                  operator: 'isNotEmpty',
                  value: ''
                },
                {
                  attribute: 'technician.name',
                  operator: 'is',
                  value: 'Ticket Triage'
                }
              ]
            }
          },
          isMSPProduct: true
        },
        fetchPolicy: 'cache-and-network',
        client: TicketClient
      }
    );

    const [getAwaitingCount, { data: awaitingTicketListData }] = useLazyQuery(
      GET_TRIAGE_TICKET_LIST,
      {
        variables: {
          listInfo: {
            sort: [
              {
                attribute: 'createdTime',
                order: 'DESC'
              }
            ],
            page: 1,
            pageSize: 3,
            condition: {
              joinOperator: 'AND',
              operands: [
                {
                  attribute: 'client.name',
                  operator: 'isNotEmpty',
                  value: ''
                },
                {
                  attribute: 'technician.name',
                  operator: 'is',
                  value: 'Ticket Triage'
                },
                {
                  attribute: 'aiAgentActionData.status',
                  operator: 'is',
                  value: 'WAITING_FOR_REQ_ACTION'
                }
              ]
            }
          },
          isMSPProduct: true
        },
        fetchPolicy: 'cache-and-network',
        client: TicketClient
      }
    );

    const triageCount =
      triageTicketListData?.getTicketList?.listInfo?.totalCount || 0;

    const awaitingCount =
      awaitingTicketListData?.getTicketList?.listInfo?.totalCount || 0;

    useEffect(() => {
      getTriageCount();
      getAwaitingCount();
    }, [props?.listInfo]);

    return (
      <div className="flex g10 actr">
        <div className="flex">
          <SVGIcon name="TriageAgent" />
        </div>
        <div>
          <Heading element="h2">
            <FormattedMessage id="common.triage.agent" />
          </Heading>
        </div>
        <div className="flex actr g10">
          <CountRender
            count={triageCount}
            label="common.working"
            dotClassName="positive-dot"
            textVariation="positive"
          />

          <CountRender
            count={awaitingCount}
            label="workspace.triage.ticket.awaiting.text"
            dotClassName="caution-dot"
            textVariation="caution"
          />
        </div>
      </div>
    );
  };

  return (
    <div className="ml20 w100 pr-20 fhp scroll pb-100">
      {isTriageAgentEnabled && isTicketViewAuthorised && (
        <div id="triage-card-carousel" className="mt20">
          <CardCarousel
            accessorKey="getTicketList"
            dataKey="tickets"
            additionalVariables={{
              isMSPProduct: isMSPProduct
            }}
            defaultSort={{
              attribute: 'createdTime',
              order: 'DESC'
            }}
            query={GET_TRIAGE_TICKET_LIST}
            defaultCondition={{
              joinOperator: 'AND',
              operands: [
                {
                  value: '',
                  operator: 'isNotEmpty',
                  attribute: 'client.name'
                },
                {
                  value: 'Ticket Triage',
                  operator: 'is',
                  attribute: 'technician.name'
                },
                {
                  attribute: 'aiAgentActionData.status',
                  operator: 'is',
                  value: 'WAITING_FOR_TECH_ACTION'
                }
              ]
            }}
            cardProps={{
              prompts: AIPrompts,
              onFeedbackComplete,
              dislikedTickets,
              ticketIdComponent: TicketDisplayIdContainer
            }}
            data={data}
            setData={setData}
            cardComponent={TriageTicketCard}
            client={TicketClient}
            headingComponent={HeadingComponent}
            refreshEventEmitters={['refreshTriageTickets']}
            emptyState={{
              nodataImage: `${envConfig.imageRoot}svg/ai-sparkle-outline.svg`,
              description: getTranslatedText(
                intl,
                'app.workspace.triage.list.emptyState.message'
              )
            }}
          />
        </div>
      )}
      <AccessControl
        roleToCheck="patchViews.view"
        render={<PatchAgent />}
        fallback={<UnauthorisedAccess />}
      />
    </div>
  );
};

export default withErrorBoundary(MonicaAIReviewQueue);
